import type { MarketplaceNotificationSettingsKey } from "@/features/admin/marketplace/types/admin-marketplace.types";

export interface SlackToggleDef {
  key: MarketplaceNotificationSettingsKey;
  labelKey: string;
  labelDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
}

export interface SlackToggleGroup {
  id: string;
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  toggles: readonly SlackToggleDef[];
}

const LIFECYCLE_TOGGLES: readonly SlackToggleDef[] = [
  {
    key: "notifyOrderSent",
    labelKey: "marketplace.admin.slackNotifications.orderSent",
    labelDefault: "Order sent to production",
    descriptionKey: "marketplace.admin.slackNotifications.orderSentHint",
    descriptionDefault: "When the partner handoff email succeeds or fails.",
  },
  {
    key: "notifyPreflightSubmitted",
    labelKey: "marketplace.admin.slackNotifications.preflightSubmitted",
    labelDefault: "Creative submitted for review",
    descriptionKey: "marketplace.admin.slackNotifications.preflightSubmittedHint",
    descriptionDefault: "Partner or preflight submission for Adge feedback.",
  },
  {
    key: "notifyRevisionRequested",
    labelKey: "marketplace.admin.slackNotifications.revisionRequested",
    labelDefault: "Revision requested",
    descriptionKey: "marketplace.admin.slackNotifications.revisionRequestedHint",
    descriptionDefault: "When ops or the client sends a script back for changes.",
  },
  {
    key: "notifyFinalApprovalSubmitted",
    labelKey: "marketplace.admin.slackNotifications.finalApproval",
    labelDefault: "Submitted for final approval",
    descriptionKey: "marketplace.admin.slackNotifications.finalApprovalHint",
    descriptionDefault: "When a script moves to client final approval.",
  },
  {
    key: "notifyOrderDelivered",
    labelKey: "marketplace.admin.slackNotifications.orderDelivered",
    labelDefault: "Order delivered",
    descriptionKey: "marketplace.admin.slackNotifications.orderDeliveredHint",
    descriptionDefault: "When fulfillment status becomes delivered.",
  },
  {
    key: "notifyOverdueDigest",
    labelKey: "marketplace.admin.slackNotifications.overdueDigest",
    labelDefault: "Daily overdue digest",
    descriptionKey: "marketplace.admin.slackNotifications.overdueDigestHint",
    descriptionDefault: "Once per UTC day when active orders are past due (hourly sweep, daily claim).",
  },
];

const ACTIVITY_TOGGLES: readonly SlackToggleDef[] = [
  {
    key: "notifyCommentAdded",
    labelKey: "marketplace.admin.slackNotifications.commentAdded",
    labelDefault: "New preflight comment",
    descriptionKey: "marketplace.admin.slackNotifications.commentAddedHint",
    descriptionDefault: "Admin, client, or partner notes — not when a comment also requests revision.",
  },
  {
    key: "notifyReactionAdded",
    labelKey: "marketplace.admin.slackNotifications.reactionAdded",
    labelDefault: "Note reaction added",
    descriptionKey: "marketplace.admin.slackNotifications.reactionAddedHint",
    descriptionDefault: "When someone adds a reaction to an order note.",
  },
  {
    key: "notifyEtaChanged",
    labelKey: "marketplace.admin.slackNotifications.etaChanged",
    labelDefault: "ETA updated",
    descriptionKey: "marketplace.admin.slackNotifications.etaChangedHint",
    descriptionDefault: "When ops changes the order due date.",
  },
  {
    key: "notifyFulfillmentStatusChanged",
    labelKey: "marketplace.admin.slackNotifications.fulfillmentChanged",
    labelDefault: "Other status changes",
    descriptionKey: "marketplace.admin.slackNotifications.fulfillmentChangedHint",
    descriptionDefault: "Fulfillment updates not covered by the lifecycle alerts above.",
  },
];

const VENDOR_TOGGLES: readonly SlackToggleDef[] = [
  {
    key: "notifyVendorConnected",
    labelKey: "marketplace.admin.slackNotifications.vendorConnected",
    labelDefault: "Partner connected",
    descriptionKey: "marketplace.admin.slackNotifications.vendorConnectedHint",
    descriptionDefault: "When a client connects to a production partner.",
  },
  {
    key: "notifyVendorTermsUpdated",
    labelKey: "marketplace.admin.slackNotifications.vendorTermsUpdated",
    labelDefault: "Partner terms updated",
    descriptionKey: "marketplace.admin.slackNotifications.vendorTermsUpdatedHint",
    descriptionDefault: "When re-sign notices are sent after a terms publish.",
  },
  {
    key: "notifyImportCompleted",
    labelKey: "marketplace.admin.slackNotifications.importCompleted",
    labelDefault: "Import committed",
    descriptionKey: "marketplace.admin.slackNotifications.importCompletedHint",
    descriptionDefault: "When a creative import batch finishes committing.",
  },
];

const RELIABILITY_TOGGLES: readonly SlackToggleDef[] = [
  {
    key: "notifyDeliveryFailures",
    labelKey: "marketplace.admin.slackNotifications.deliveryFailures",
    labelDefault: "Delivery failures",
    descriptionKey: "marketplace.admin.slackNotifications.deliveryFailuresHint",
    descriptionDefault: "Slack when a lifecycle notification email or publish leg fails.",
  },
];

export const SLACK_TOGGLE_GROUPS: readonly SlackToggleGroup[] = [
  {
    id: "lifecycle",
    titleKey: "marketplace.admin.slackNotifications.lifecycleTitle",
    titleDefault: "Order lifecycle",
    descriptionKey: "marketplace.admin.slackNotifications.lifecycleDescription",
    descriptionDefault: "Alerts that mirror major handoff and delivery milestones.",
    toggles: LIFECYCLE_TOGGLES,
  },
  {
    id: "activity",
    titleKey: "marketplace.admin.slackNotifications.activityTitle",
    titleDefault: "Production activity",
    descriptionKey: "marketplace.admin.slackNotifications.activityDescription",
    descriptionDefault: "Day-to-day changes on active orders (no comment text or emails in Slack).",
    toggles: ACTIVITY_TOGGLES,
  },
  {
    id: "vendors",
    titleKey: "marketplace.admin.slackNotifications.vendorTitle",
    titleDefault: "Partners & imports",
    descriptionKey: "marketplace.admin.slackNotifications.vendorDescription",
    descriptionDefault: "Connection, terms, and bulk import events.",
    toggles: VENDOR_TOGGLES,
  },
  {
    id: "reliability",
    titleKey: "marketplace.admin.slackNotifications.reliabilityTitle",
    titleDefault: "Reliability",
    descriptionKey: "marketplace.admin.slackNotifications.reliabilityDescription",
    descriptionDefault: "When email or publish legs fail after a lifecycle event.",
    toggles: RELIABILITY_TOGGLES,
  },
];

export const ALL_SLACK_TOGGLE_KEYS: readonly MarketplaceNotificationSettingsKey[] =
  SLACK_TOGGLE_GROUPS.flatMap((group) => group.toggles.map((toggle) => toggle.key));

export type SlackToggleSettings = Record<MarketplaceNotificationSettingsKey, boolean>;

export function countEnabledSlackToggles(
  settings: SlackToggleSettings,
  keys: readonly MarketplaceNotificationSettingsKey[] = ALL_SLACK_TOGGLE_KEYS,
): number {
  return keys.reduce((count, key) => count + (settings[key] ? 1 : 0), 0);
}

export function toSlackTogglePatch(
  keys: readonly MarketplaceNotificationSettingsKey[],
  value: boolean,
): Partial<SlackToggleSettings> {
  const patch: Partial<SlackToggleSettings> = {};
  for (const key of keys) {
    patch[key] = value;
  }
  return patch;
}
